// GA4 e-commerce events (TTC amounts, EUR). gtag is always loaded (Consent Mode v2: cookieless pings until consent is granted).
const CURRENCY = 'EUR';
const n2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

export const gaEvent = (name, params = {}) => {
  if (typeof window.gtag !== 'function') return;
  try { window.gtag('event', name, params); } catch { /* analytics must never break the UI */ }
};

export const gaItem = (p, quantity = 1, extra = {}) => ({
  item_id: p.product_id || p.id,
  item_name: p.name,
  item_brand: p.brand,
  ...(p.category ? { item_category: p.category } : {}),
  ...(p.subcategory ? { item_category2: p.subcategory } : {}),
  price: n2(p.price),
  quantity: Number(quantity) || 1,
  ...extra,
});

const value = (items) => n2(items.reduce((s, i) => s + i.price * i.quantity, 0));
let lastList = '';
let lastCheckout = '';

let lastSearch = '';

export const ga = {
  // GA4 recommended `search` event + custom params (search_type: parts | suggestion | plate | vehicle ; search_results). Plates are never sent (personal data).
  search: (term, type, extra = {}) => {
    const key = `${type}:${term}:${extra.search_results ?? ''}`;
    if (key === lastSearch) return;
    lastSearch = key;
    gaEvent('search', { search_term: String(term || '').slice(0, 100), search_type: type, ...extra });
  },
  // GA4 recommended `share` event — method: whatsapp | copy_link | native | facebook ; content_type: vehicle_selection | product
  share: (method, contentType, itemId, extra = {}) => gaEvent('share', { method, content_type: contentType, item_id: itemId, ...extra }),
  viewItem: (p) => gaEvent('view_item', { currency: CURRENCY, value: n2(p.price), items: [gaItem(p)] }),
  viewItemList: (listId, listName, products) => {
    const key = `${listId}:${products.length}:${products[0]?.id || ''}`;
    if (!products.length || key === lastList) return;
    lastList = key;
    gaEvent('view_item_list', { item_list_id: listId, item_list_name: listName, items: products.slice(0, 50).map((p, i) => gaItem(p, 1, { index: i })) });
  },
  addToCart: (p, quantity = 1) => gaEvent('add_to_cart', { currency: CURRENCY, value: n2(p.price * quantity), items: [gaItem(p, quantity)] }),
  removeFromCart: (p, quantity = 1) => gaEvent('remove_from_cart', { currency: CURRENCY, value: n2(p.price * quantity), items: [gaItem(p, quantity)] }),
  beginCheckout: (cartItems, coupon) => {
    const items = cartItems.map((i) => gaItem(i, i.quantity));
    const key = items.map((i) => `${i.item_id}x${i.quantity}`).join('|');
    if (!items.length || key === lastCheckout) return;
    lastCheckout = key;
    gaEvent('begin_checkout', { currency: CURRENCY, value: value(items), ...(coupon ? { coupon } : {}), items });
  },
  addPaymentInfo: (cartItems, total, coupon, paymentType = 'card') => gaEvent('add_payment_info', { currency: CURRENCY, value: n2(total), payment_type: paymentType, ...(coupon ? { coupon } : {}), items: cartItems.map((i) => gaItem(i, i.quantity)) }),
  // `summary` = payload from /payments/sumup/confirm (transaction_id, value, shipping, coupon, items). Sent once per order.
  purchase: (summary) => {
    if (!summary?.transaction_id) return;
    const key = `gaPurchase:${summary.transaction_id}`;
    if (localStorage.getItem(key)) return;
    const vat = n2(summary.value - summary.value / 1.2);
    gaEvent('purchase', { transaction_id: summary.transaction_id, currency: CURRENCY, value: n2(summary.value), tax: vat, shipping: n2(summary.shipping), ...(summary.coupon ? { coupon: summary.coupon } : {}),
      items: (summary.items || []).map((i) => gaItem(i, i.quantity)) });
    localStorage.setItem(key, '1');
  },
};
